/* eslint-disable sonarjs/no-nested-template-literals */
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CommissionsDetailResponse,
  DashboardMetaData,
  DoubleDashResponse,
  EzShipProjectionDetailsResponse,
  FirstOrderBonusDetailsResponse,
  PetQuizCountResponse,
  RecentOrdersResponse,
  SponsoringApiResponse,
  VolumnsDetailsResponse,
} from '@/types/Dashboard';
import { ResponseError } from '@/types/Error';
import { VolumeBucketResponse } from '@/types/Reports';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const applicationJson = 'application/json';

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: 'dashboard' }),
  tagTypes: ['LeaderBoard'],
  endpoints: builder => ({
    getLeaderboard: builder.query<SuccessResponse<SponsoringApiResponse>, void>({
      query: () => ({
        url: `/LeaderBoard`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<SponsoringApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getRecentOrders: builder.query<SuccessResponse<RecentOrdersResponse>, void>({
      query: () => ({
        url: `/GetRecentOrders`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<RecentOrdersResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPetQuizCount: builder.query<SuccessResponse<PetQuizCountResponse>, void>({
      query: () => ({
        url: `/petquizcount`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PetQuizCountResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getFirstOrderBonusDetails: builder.query<SuccessResponse<FirstOrderBonusDetailsResponse>, void>({
      query: () => ({
        url: `/FirstOrderBonusDetails`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<FirstOrderBonusDetailsResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getVolumnsDetails: builder.query<SuccessResponse<VolumnsDetailsResponse>, void>({
      query: () => ({
        url: `/GetVolumes`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<VolumnsDetailsResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDashboardMetaData: builder.query<SuccessResponse<DashboardMetaData>, void>({
      query: () => ({
        url: `/GetDashboardMetaData`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DashboardMetaData>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getEzShipProjectionDetails: builder.query<SuccessResponse<EzShipProjectionDetailsResponse>, void>({
      query: () => ({
        url: `/EzShipProjectionDetails`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<EzShipProjectionDetailsResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getEZPVVolumeBucketDetails: builder.query<SuccessResponse<VolumeBucketResponse>, string>({
      query: url => ({
        url,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<VolumeBucketResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCommissionsDetails: builder.mutation<SuccessResponse<CommissionsDetailResponse>, { selectedyear: number }>({
      query: ({ selectedyear }) => ({
        url: `/getcurrentearnings?selectedyear=${selectedyear}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CommissionsDetailResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDoubleDash: builder.query<SuccessResponse<DoubleDashResponse>, void>({
      query: () => ({
        url: `/DoubleDash`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DoubleDashResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetLeaderboardQuery,
  useGetRecentOrdersQuery,
  useGetPetQuizCountQuery,
  useGetFirstOrderBonusDetailsQuery,
  useGetVolumnsDetailsQuery,
  useGetDashboardMetaDataQuery,
  useLazyGetEZPVVolumeBucketDetailsQuery,
  useGetCommissionsDetailsMutation,
  useGetEzShipProjectionDetailsQuery,
  useGetDoubleDashQuery,
} = dashboardApi;
